import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CostCenter } from '@/types/financial';
import { useToast } from '@/hooks/use-toast';
import { useMultiTenant } from '@/hooks/useMultiTenant';

const EMPTY_CENTERS: CostCenter[] = [];

export const useCostCenters = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { companyId } = useMultiTenant();

  const query = useQuery({
    queryKey: ['cost-centers', companyId],
    queryFn: async (): Promise<CostCenter[]> => {
      if (!companyId) throw new Error('Company ID não disponível');

      const { data, error } = await supabase
        .from('cost_centers')
        .select('*')
        .eq('company_id', companyId)
        .eq('active', true)
        .order('center_name');

      if (error) throw error;
      return (data || []) as CostCenter[];
    },
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
  });

  const createMutation = useMutation({
    mutationFn: async (center: Omit<CostCenter, 'id' | 'created_at' | 'created_by' | 'company_id'>) => {
      if (!companyId) throw new Error('Company ID não disponível');

      const { data, error } = await supabase
        .from('cost_centers')
        .insert([{ ...center, company_id: companyId }])
        .select()
        .single();

      if (error) throw error;
      return data as CostCenter;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-centers'] });
      toast({ title: 'Centro de custo criado' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao criar centro de custo',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    // A-sept.2: tenant/autoria nunca reatribuíveis pelo UPDATE — descartados do objeto
    mutationFn: async ({
      id,
      company_id: _companyId,
      created_at: _createdAt,
      created_by: _createdBy,
      ...updates
    }: Partial<CostCenter> & { id: string }) => {
      if (!companyId) throw new Error('Company ID não disponível');

      const { data, error } = await supabase
        .from('cost_centers')
        .update(updates)
        .eq('id', id)
        .eq('company_id', companyId)
        .select()
        .single();

      if (error) throw error;
      return data as CostCenter;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['cost-centers'] });
      queryClient.invalidateQueries({ queryKey: ['cost-center', variables.id] });
      toast({ title: 'Centro de custo atualizado' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao atualizar centro de custo',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!companyId) throw new Error('Company ID não disponível');

      const { error } = await supabase
        .from('cost_centers')
        .update({ active: false })
        .eq('id', id)
        .eq('company_id', companyId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-centers'] });
      toast({ title: 'Centro de custo excluído' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao excluir centro de custo',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    centers: query.data ?? EMPTY_CENTERS,
    isLoading: query.isLoading,
    createCenter: createMutation.mutate,
    updateCenter: updateMutation.mutate,
    deleteCenter: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
